import { Box, Typography } from '@mui/material'
import _ from 'lodash'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** COMPONENT */
import PrivilegeList from 'features/finance/payment/component/popupPayment/PrivilegeList'

/** UTILS */
import {
  setSummerySubByKey,
  setSummerySubTreatmentRightByKey,
  setSummeryTreatmentRightByKey,
  summerySub,
  summerySubTreatmentRight,
  summeryTreatmentRight,
} from 'app/slice/payment.slice'
import { numberFormat } from 'utils/app.utils'


type TreatmentDiscountType = {
  treatments?: any
  treatmentRights: any[]
  treatmentRightValue?: any
  paymentChannelId: number
  balancePaid?: number
  menu: 'PAYMENT' | 'OVERDUE' | 'RETROSPECTIVE' | 'EDIT_PAYMENT'
  disableFiled?: boolean | false
  disableIcon?: boolean
  onChangePaid?: (val: number) => void
  dataPatientCase?: any
}

function TreatmentDiscount(props: TreatmentDiscountType) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const valSummerySub = useSelector(summerySub)
  const valSummeryTreatmentRight = useSelector(summeryTreatmentRight)
  const valSummerySubTreatmentRight = useSelector(summerySubTreatmentRight)

  const [trPaid, setTrPaid] = useState<any[]>([])

  // ✅ FIX: ใช้ ref เพื่อป้องกัน infinite loop
  const isProcessingRef = useRef(false)
  const processedDataRef = useRef<string>('')

  // ✅ OPTIMIZED: Memoized initial treatment rights mapping
  const initialTrPaid = useMemo(() =>
    _.map(props.treatmentRights, (tr: any) => ({ trId: tr.patientTreatmentRightId, value: 0 })),
    [props.treatmentRights]
  )

  useEffect(() => {
    setTrPaid(initialTrPaid)
  }, [initialTrPaid])

  // ✅ FIX: แก้ไข infinite loop ด้วย ref และ deep comparison
  useEffect(() => {
    // ป้องกันการทำงานซ้ำขณะที่กำลัง process อยู่
    if (isProcessingRef.current) return

    const hasTreatmentRightChannel5 = valSummeryTreatmentRight[5]
    if (!hasTreatmentRightChannel5) return

    const pcId: any = 5

    if (!valSummerySubTreatmentRight[pcId]) return

    // ✅ FIX: เช็คว่าข้อมูลเปลี่ยนแปลงจริงๆ หรือไม่
    const currentDataKey = JSON.stringify({
      subTreatmentRight: valSummerySubTreatmentRight[pcId],
      treatmentRight: hasTreatmentRightChannel5
    })

    // ถ้าข้อมูลไม่เปลี่ยนแปลง ไม่ต้องทำอะไร
    if (processedDataRef.current === currentDataKey) return

    // เริ่มต้น processing
    isProcessingRef.current = true
    processedDataRef.current = currentDataKey

    const objBalance: any = {}
    const objValue: any = {}
    const dispatchActions: any[] = []

    for (const ptrId in valSummerySubTreatmentRight[pcId]) {
      if (Object.prototype.hasOwnProperty.call(valSummerySubTreatmentRight[pcId], ptrId)) {
        const strSub = valSummerySubTreatmentRight[pcId][ptrId];
        for (const index in strSub) {
          if (Object.prototype.hasOwnProperty.call(strSub, index)) {
            const valSub = strSub[index];
            const { value, treatmentPrice, paidBalance } = valSub

            if (objBalance[index] === undefined) objBalance[index] = paidBalance
            if (objValue[index] === undefined) objValue[index] = 0

            // เช็คว่า paidBalance ต้องอัปเดตจริงๆ หรือไม่
            if (valSub.paidBalance !== objBalance[index]) {
              dispatchActions.push({
                type: 'setSummerySubTreatmentRightByKey',
                payload: {
                  key: pcId,
                  keySub: index,
                  patientTreatmentRightId: ptrId,
                  value: { ...valSub, paidBalance: objBalance[index] }
                }
              })
            }

            objBalance[index] -= value
            objValue[index] += value
          }
        }
      }
    }

    _.map(objBalance, (balance: any, index: any) => {
      const sub: any = valSummerySub[pcId] !== undefined ? valSummerySub[pcId][index] : null
      if (sub && sub.value !== objValue[index]) {
        dispatchActions.push({
          type: 'setSummerySubByKey',
          payload: { key: pcId, keySub: index, value: { ...sub, value: objValue[index] } }
        })
      }
    })

    // ✅ FIX: Batch dispatch เฉพาะที่มีการเปลี่ยนแปลง
    if (dispatchActions.length > 0) {
      dispatchActions.forEach(action => {
        if (action.type === 'setSummerySubTreatmentRightByKey') {
          dispatch(setSummerySubTreatmentRightByKey(action.payload))
        } else if (action.type === 'setSummerySubByKey') {
          dispatch(setSummerySubByKey(action.payload))
        }
      })
    }

    // เสร็จสิ้นการ processing
    isProcessingRef.current = false
  }, [valSummeryTreatmentRight, valSummerySubTreatmentRight, valSummerySub, dispatch])

  useEffect(() => {
    _.map(props.treatmentRights, (treatmentRight, index) => {
      dispatch(setSummeryTreatmentRightByKey({ key: props.paymentChannelId, patientTreatmentRightId: treatmentRight.patientTreatmentRightId, value: { isCheck: false, value: 0, isDisabled: true } }))
    })
  }, [props.paymentChannelId, props.treatmentRights, dispatch])

  // ✅ OPTIMIZED: Memoized change paid handler
  const handleChangePaid = useCallback((treatmentRightId: number) => (val: number) => {
    setTrPaid(prev => [
      ...prev.filter((trP: any) => trP.trId !== treatmentRightId),
      { trId: treatmentRightId, value: val }
    ])
  }, [])

  // ✅ OPTIMIZED: Memoized treatment rights with no discount message
  const hasNoTreatmentRights = useMemo(() =>
    props.treatmentRights.length === 0,
    [props.treatmentRights.length]
  )

  return (
    <Box className='mb-3'>
      <Typography className='mt-3' sx={{ fontWeight: 500 }}>{t('PAYMENT.FORM_PAYMENT.TREATMENT_DISCOUNT')}</Typography>
      <Box className='pl-xl-3' sx={{ marginTop: '-8px' }}>
        {!hasNoTreatmentRights ? (
          <>
            {_.map(props.treatmentRights, (treatmentRight, index) => {
              return (
                <PrivilegeList
                  disableIcon={true}
                  type='TreatmentDiscount'
                  menu={props.menu}
                  paymentChannelId={props.paymentChannelId}
                  changePaid={handleChangePaid(treatmentRight.patientTreatmentRightId)}
                  patientTreatmentRightId={treatmentRight.patientTreatmentRightId}
                  treatmentRight={treatmentRight}
                  treatmentRightValue={props.treatmentRightValue}
                  index={index}
                  key={treatmentRight.patientTreatmentRightId || index}
                  title={props.dataPatientCase?.isNhso !== '1' ? t('PAYMENT.FORM_PAYMENT.TITLE_DISCOUNT', { privilege: treatmentRight.treatmentRightName, price: numberFormat(treatmentRight.balance, 0, 0), unit: treatmentRight.isDivide === '1' ? (treatmentRight.unit === 'BAHT' && t('CURRENCY_CHANGE.TH') || '%') : t('PAYMENT.FORM_PAYMENT.TIMES') }) : `${treatmentRight.treatmentRightName}`}
                  treatments={props.treatments}
                  disableFiled={props.disableFiled}
                  treatmentInfo={
                    t('PAYMENT.FORM_PAYMENT.TREATMENT_DISCOUNT_INFO', {
                      price: treatmentRight.isDivide === '1' ? treatmentRight.balance : treatmentRight.price,
                      unit: treatmentRight.unit === 'BAHT' ? ` ${t('CURRENCY_CHANGE.TH')}` : '%',
                      balance: treatmentRight.isDivide === '1' ? 1 : treatmentRight.balance,
                      unitIsDivide: treatmentRight.isDivide === '1' ? t('PAYMENT.FORM_PAYMENT.RIGHT') : t('PAYMENT.FORM_PAYMENT.TIMES'),
                      isNoDF: treatmentRight.isDoctorFee === '1' ? '' : t('PAYMENT.FORM_PAYMENT.OVERDUE_CONFIRM.NO')
                    })}
                  discountHasExpired={treatmentRight.balance < 1}
                  dataPatientCase={props.dataPatientCase}
                />
              )
            })}
          </>
        ) : (<div className='text-center p-2 mt-2 text-muted'>{t('PAYMENT.FORM_PAYMENT.NO_TREATMENT_DISCOUNT')}</div>)}
      </Box>
    </Box>
  )
}

// ✅ OPTIMIZED: Memoized component to prevent unnecessary re-renders
export default memo(TreatmentDiscount)

